import { useEffect, useRef, useState } from "react";

/**
 * CountUp — animates a number from 0 to `to` when it enters the viewport.
 * `to` can include a suffix like "99%" or "133+". Non-numeric text is preserved.
 */
export default function CountUp({ to, duration = 1600, className = "" }) {
  const ref = useRef(null);
  const [current, setCurrent] = useState(0);
  const [started, setStarted] = useState(false);

  // Extract number + optional prefix/suffix
  const match = String(to).match(/^(\D*)([\d.]+)(\D*)$/);
  const prefix = match ? match[1] : "";
  const target = match ? parseFloat(match[2]) : null;
  const suffix = match ? match[3] : String(to);
  const isNumeric = target !== null && !Number.isNaN(target);

  useEffect(() => {
    if (!isNumeric || !ref.current) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started) {
          setStarted(true);
          const start = performance.now();
          const step = (now) => {
            const t = Math.min(1, (now - start) / duration);
            const eased = 1 - Math.pow(1 - t, 3); // ease-out cubic
            setCurrent(target * eased);
            if (t < 1) requestAnimationFrame(step);
            else setCurrent(target);
          };
          requestAnimationFrame(step);
          obs.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [isNumeric, target, duration, started]);

  if (!isNumeric) {
    return <span ref={ref} className={className}>{to}</span>;
  }
  const display = Number.isInteger(target)
    ? Math.round(current).toString()
    : current.toFixed(1);
  return (
    <span ref={ref} className={className}>{prefix}{display}{suffix}</span>
  );
}
